import { TGetWishlistSuggestions } from '@daymade/api-contracts';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { TWishlistPrize } from 'stores/UserStore';
import { tryApi } from 'utils/api/common';
import { prizeApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import AboutUsSiteInnerContainer from '../../about-us/AboutUsSiteInnerContainer';
import DashboardComponentHeader from '../common/DashboardComponentHeader';
import DashboardComponentLink from '../common/DashboardComponentLink';
import EmptyPrize from './EmptyPrize';
import SuggestedPrize from './SuggestedPrize';
import UnfavouritePrizeModal from './UnfavouritePrizeModal';
import WishlistPrize from './WishlistPrize';
import styles from './YourWishlist.module.css';

interface Props {
  fullWishlist: TWishlistPrize[];
  shortlistSuggestedPrizes: TGetWishlistSuggestions | undefined;
  selectedPrize: TWishlistPrize | undefined;
  openUnfavouritePrizeModal: (prize: TWishlistPrize) => void;
  unfavouritePrize: (prize: TWishlistPrize) => Promise<void>;
  favouritePrize: (prize: TGetWishlistSuggestions[number]) => Promise<void>;
  closeModal: () => void;
  desktopPrizesDirection?: 'column' | 'row';
}

export const YourWishlist = (props: Props) => {
  const router = useRouter();
  const firstSuggestedPrize = props.shortlistSuggestedPrizes?.[0];
  const desktopPrizesDirection = props.desktopPrizesDirection ?? 'row';

  return (
    <AboutUsSiteInnerContainer background="white" borderRadius withPadding>
      {props.selectedPrize && (
        <UnfavouritePrizeModal
          prize={props.selectedPrize}
          handleOnClose={() => props.closeModal()}
          unfavouritePrize={(prize) => props.unfavouritePrize(prize)}
        />
      )}
      <div className={styles.container}>
        <DashboardComponentHeader
          title="Your wishlist"
          subtitle="The prizes you're dreaming of all in one place"
          separator
        />
        <div className={styles.content}>
          <div
            className={`${styles.prizes} ${
              desktopPrizesDirection === 'row'
                ? styles.prizesDesktopRow
                : styles.prizesDesktopColumn
            }`}
          >
            {props.fullWishlist?.length > 0
              ? props.fullWishlist?.slice(0, 3).map((prize, i) => (
                  <div
                    key={`${i}-wishlist-item`}
                    className={styles.prizeFlexItem}
                  >
                    <WishlistPrize
                      prize={prize}
                      favourited
                      unfavouritePrize={() =>
                        props.openUnfavouritePrizeModal(prize)
                      }
                    />
                  </div>
                ))
              : null}

            {props.fullWishlist?.length < 3 && (
              <div className={styles.prizeFlexItem}>
                <EmptyPrize key={`empty-item`} router={router} />
              </div>
            )}

            {props.shortlistSuggestedPrizes &&
            props.shortlistSuggestedPrizes?.length > 0 ? (
              <>
                {props.fullWishlist?.length == 0 &&
                  props.shortlistSuggestedPrizes?.map((prize, i) => (
                    <div
                      key={`${i}-suggested-item`}
                      className={styles.prizeFlexItem}
                    >
                      <SuggestedPrize
                        prize={prize}
                        favouritePrize={() => props.favouritePrize(prize)}
                      />
                    </div>
                  ))}
                {props.fullWishlist?.length == 1 && firstSuggestedPrize && (
                  <div className={styles.prizeFlexItem}>
                    <SuggestedPrize
                      prize={firstSuggestedPrize}
                      favouritePrize={() =>
                        props.favouritePrize(firstSuggestedPrize)
                      }
                    />
                  </div>
                )}
              </>
            ) : null}
          </div>
          <DashboardComponentLink
            linkText={
              props.fullWishlist.length > 3 ? 'View all' : 'View more prizes'
            }
            href={
              props.fullWishlist.length > 3 ? '/account/wish-list' : '/prizes'
            }
          />
        </div>
      </div>
    </AboutUsSiteInnerContainer>
  );
};

interface CurrentUserYourWishlistProps {
  desktopPrizesDirection?: 'column' | 'row';
}

export const CurrentUserYourWishlist = observer(
  (props: CurrentUserYourWishlistProps) => {
    const userStore = useUserStore();
    const [fullWishlist, setFullWishlist] = useState(userStore.wishListPrizes);
    const [suggestedPrizes, setSuggestedPrizes] =
      useState<TGetWishlistSuggestions>();
    const [shortlistSuggestedPrizes, setShortlistSuggestedPrizes] =
      useState<TGetWishlistSuggestions>();
    const [selectedPrize, setSelectedPrize] = useState<TWishlistPrize>();

    useEffect(() => {
      const getSuggestedPrizes = async () => {
        const suggestions = await tryApi(
          prizeApiClient.auth.getWishlistSuggestions({
            query: { limit: 2 },
          }),
        );

        if (suggestions.status === HttpStatusCode.OK) {
          setSuggestedPrizes(suggestions.body);
        } else if (suggestions.status === 'error') {
          console.error('Error fetching suggested prizes:', suggestions.error);
        }
      };

      getSuggestedPrizes();
    }, []);

    useEffect(() => {
      if (suggestedPrizes && userStore.wishListPrizes) {
        const idsInWishlist = new Set(
          userStore.wishListPrizes.map((p) => p.id),
        );

        const filteredSuggestions = suggestedPrizes.filter(
          (prize) => !idsInWishlist.has(prize.id),
        );
        setShortlistSuggestedPrizes(filteredSuggestions);
      }

      if (userStore.userInitialized && userStore.wishListPrizes) {
        setFullWishlist(userStore.wishListPrizes);
      }
    }, [suggestedPrizes, userStore.wishListPrizes, userStore.userInitialized]);

    const openUnfavouritePrizeModal = (prize: TWishlistPrize) => {
      setSelectedPrize(prize);
    };

    const closeModal = () => {
      setSelectedPrize(undefined);
    };

    const unfavouritePrize = async (prize: TWishlistPrize) => {
      const response = await prizeApiClient.auth.unfavouritePrize({
        body: {
          prizeId: prize.id,
        },
      });
      closeModal();

      if (response.status === HttpStatusCode.OK) {
        setFullWishlist((prevState) => {
          const shortenedWishlist = prevState.filter((p) => p.id !== prize.id);
          return shortenedWishlist;
        });
        userStore.fetchWishListPrizes();
      } else {
        throw new Error(
          'Currently unable to remove wishlist prize. Try again later.',
        );
      }
    };

    const favouritePrize = async (prize: TGetWishlistSuggestions[number]) => {
      const response = await prizeApiClient.auth.favouritePrize({
        body: {
          prizeId: prize.id,
        },
      });

      if (response.status === HttpStatusCode.OK) {
        userStore.fetchWishListPrizes();
      } else {
        throw new Error(
          'Currently unable to add wishlist prize. Try again later.',
        );
      }
    };

    return (
      <YourWishlist
        fullWishlist={fullWishlist}
        shortlistSuggestedPrizes={shortlistSuggestedPrizes}
        selectedPrize={selectedPrize}
        openUnfavouritePrizeModal={openUnfavouritePrizeModal}
        unfavouritePrize={unfavouritePrize}
        favouritePrize={favouritePrize}
        closeModal={closeModal}
        desktopPrizesDirection={props.desktopPrizesDirection}
      />
    );
  },
);

export default CurrentUserYourWishlist;
